import {
  Alert,
  Button,
  Card,
  Typography
} from 'antd';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LockOutlined } from '@ant-design/icons';
import SeeWardLogo from '../../../assets/image/logoSeeWard.png';
import { login as loginService } from '../../../service/auth/auth.service';
import { messageFor } from '../../../service/mfa.service';
import { useDispatch } from 'react-redux';
import { fetchUser, loginSucceeded } from '../../../redux/auth/actions';
import CodeInput from '../../UI/input/CodeInput';
import LostAuthenticator from './LostAuthenticator';

/**
 * The second-factor challenge: the code from the authenticator app, re-POSTed to `/login` as
 * `otp`. A correct one answers with a session. The authenticator is the only second factor.
 *
 * Everything this screen needs arrives through router state, in memory. It used to arrive
 * through `localStorage`, including the user's **plaintext password**. Losing all of it on a
 * refresh is correct: the recovery from here is to sign in again.
 */

interface ChallengeState {
  email?: string;
  password?: string;
}

const TwoFactorFeature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const state = (location.state ?? {}) as ChallengeState;

  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLostAppHelp, setShowLostAppHelp] = useState(false);

  // No state means a refresh, a bookmark, or a direct visit. There is nothing to challenge
  // against, so start over.
  useEffect(() => {
    if (!state.email || !state.password) {
      navigate('/login', { replace: true });
    }
  }, [state.email, state.password, navigate]);

  const restart = (reason: string) => {
    navigate('/login', {
      replace: true,
      state: {
        notice: reason,
        noticeType: 'warning'
      }
    });
  };

  const completeSignIn = async (data: any) => {
    await dispatch<any>(loginSucceeded(data));
    await dispatch<any>(fetchUser());

    const lastVisitedPath = localStorage.getItem('lastVisitedPath');
    if (lastVisitedPath) {
      localStorage.removeItem('lastVisitedPath');
      navigate(lastVisitedPath);
    } else {
      navigate('/');
      window.location.reload();
    }
  };

  // TOTP: the code goes back to `/login` as `otp`. A correct one answers with a session.
  const submitTotp = async (submitted: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await loginService({
        email: state.email!,
        password: state.password!,
        otp: submitted
      });
      const data = response?.data ?? {};

      if (data.access_token) {
        await completeSignIn(data);
        return;
      }

      // No token means the code was wrong.
      setCode('');
      setError('That code is not right. Check your authenticator app and try again.');
    } catch (err: any) {
      setCode('');
      setError(
        messageFor(err, {
          401: 'That code is not right. Check your authenticator app and try again.',
          429: 'Too many attempts. Please wait a minute and try again.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLostApp = showLostAppHelp;

  return (
    <div
      className="flex min-h-screen flex-col justify-center items-center px-4 py-10"
    >
      <div className="w-full max-w-[480px]">
        <div className="flex justify-center mb-8">
          <a href="/">
            <img className="h-10 w-auto" src={SeeWardLogo} alt="SeeWard" />
          </a>
        </div>

        <Card>
          <Typography.Title level={4} className="!mb-1">
            {isLostApp ? 'Cannot use your authenticator?' : 'Two-factor authentication'}
          </Typography.Title>

          {isLostApp ? (
            <LostAuthenticator
              email={state.email!}
              password={state.password!}
              onSignedIn={completeSignIn}
              onAccountGone={restart}
              onBack={() => {
                setShowLostAppHelp(false);
                setCode('');
                setError(null);
              }}
            />
          ) : (
            <>
              <Typography.Paragraph type="secondary" className="text-[13px]">
                Enter the 6-digit code from your authenticator app.
              </Typography.Paragraph>

              {error && (
                <Alert
                  type="error"
                  showIcon
                  className="mb-4"
                  message={error}
                  closable
                  onClose={() => setError(null)}
                />
              )}
              <Alert
                type="info"
                showIcon
                className="mb-4"
                message={
                  <span className="text-[13px]">
                    Please ensure your device time is accurate. Codes expire quickly.
                  </span>
                }
              />

              <div className="flex justify-center my-6">
                <CodeInput
                  value={code}
                  onChange={setCode}
                  onComplete={submitTotp}
                  disabled={isSubmitting}
                  autoFocus
                  status={error ? 'error' : undefined}
                />
              </div>

              <Button
                type="primary"
                size="large"
                block
                loading={isSubmitting}
                disabled={code.length !== 6}
                onClick={() => submitTotp(code)}
              >
            Verify code
              </Button>

              <div className="flex items-center justify-between gap-2 mt-4">
                <Button
                  type="link"
                  className="!px-0"
                  onClick={() => navigate('/login', { replace: true })}
                >
              Back to sign in
                </Button>
                <Button
                  type="link"
                  className="!px-0"
                  onClick={() => setShowLostAppHelp(true)}
                >
                  Cannot use your app?
                </Button>
              </div>
            </>
          )}
        </Card>

        <Typography.Paragraph
          type="secondary"
          className="text-center text-[13px] mt-6"
        >
          <LockOutlined className="mr-2" />
          {isLostApp
            ? 'Your password is still required either way. Only the second step changes.'
            : 'No code? Open your authenticator app, or sign in again to start over.'}
        </Typography.Paragraph>
      </div>
    </div>
  );
};

export default TwoFactorFeature;
